import React, { useState, useEffect, useRef } from 'react';
import './DialogStyles.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface BottomSheetSelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  title?: string; // Screen reader / Bottom Sheet header title
  style?: React.CSSProperties;
}

export const BottomSheetSelect: React.FC<BottomSheetSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select an option...',
  disabled = false,
  title = 'Select Option',
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Track responsive screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside on Desktop to close popover
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile]);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (optValue: string, optDisabled?: boolean) => {
    if (optDisabled) return;
    onChange(optValue);
    setIsOpen(false);
  };

  // Find the selected option's label
  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  return (
    <div 
      className="custom-select-wrapper" 
      ref={wrapperRef}
      style={style}
    >
      {/* Trigger Button */}
      <button
        type="button"
        className={`custom-select-trigger ${disabled ? 'disabled' : ''}`}
        onClick={handleTriggerClick}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{displayLabel}</span>
        <span className={`custom-select-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {/* DESKTOP POPDOWN VIEWPORT */}
      {isOpen && !isMobile && (
        <div className="custom-select-dropdown" role="listbox">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={value === opt.value}
              className={`custom-select-option ${value === opt.value ? 'selected' : ''} ${opt.disabled ? 'disabled' : ''}`}
              onClick={() => handleSelectOption(opt.value, opt.disabled)}
              disabled={opt.disabled}
            >
              <span>{opt.label}</span>
              {value === opt.value && <span>✓</span>}
            </button>
          ))}
        </div>
      )}

      {/* MOBILE BOTTOM SHEET VIEWPORT */}
      {isOpen && isMobile && (
        <div 
          className="bottom-sheet-overlay" 
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="bottom-sheet-container" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top rounded Grab Handle */}
            <div className="bottom-sheet-handle" />

            <div className="bottom-sheet-header">
              <h4 className="bottom-sheet-title">{title}</h4>
            </div>

            <div className="bottom-sheet-content">
              {options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`bottom-sheet-option ${value === opt.value ? 'selected' : ''} ${opt.disabled ? 'disabled' : ''}`}
                  onClick={() => handleSelectOption(opt.value, opt.disabled)}
                  disabled={opt.disabled}
                >
                  <span>{opt.label}</span>
                  {value === opt.value && <span>✓</span>}
                </button>
              ))}
            </div>

            <div className="bottom-sheet-footer">
              <button
                type="button"
                className="bottom-sheet-cancel-btn"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
